// src/components/forms/shared/BookingCardKennelList.tsx
// Displays booking kennels with heater details (rate, nights, total)
// Reuses SummaryRow component for consistency with BookingSummary

import React from 'react';
import { useNavigate } from 'react-router-dom';
import { Box, IconButton, Typography, useTheme } from '@mui/material';
import SummaryRow from './SummaryRow';
import HomeIcon from '@mui/icons-material/Home';
import CalendarMonthIcon from '@mui/icons-material/CalendarMonth';
import { getCustomIcon } from '../../../utils/customIcons';

interface BookingKennelResponse {
  id: number;
  kennelNumber: number;
  kennelSide: string;
  kennelSideId: number;
  size?: string;
  heaterNights: number;
  heaterDailyRate: number;
}

interface BookingCardKennelListProps {
  kennels: BookingKennelResponse[];
  bookingId: number;
  checkInDate: string;
}

const formatCurrency = (amount: number): string => {
  return new Intl.NumberFormat('en-ZA', {
    style: 'currency',
    currency: 'ZAR',
    minimumFractionDigits: 2,
    maximumFractionDigits: 2,
  }).format(amount);
};

const getKennelLabel = (kennel: BookingKennelResponse): string => {
  // Special cases: Animal Room variations
  if (kennel.kennelSide.toLowerCase().includes('animal')) {
    return 'Animal Room';
  }

  // Special cases: Cattery Communal
  if (kennel.kennelSide === 'Cattery Communal') {
    return kennel.kennelSide;
  }

  // Remove "Side" prefix from kennelSide (e.g., "SideA" â†’ "A")
  const sidePrefix = kennel.kennelSide.replace(/^Side/, '');
  return `${sidePrefix}${kennel.kennelNumber} - ${kennel.size || ''}`.trim();
};

const deriveTabLabel = (kennelSide: string, size?: string): string => {
  if (kennelSide.toLowerCase().includes('cattery')) return 'Cattery';
  const letter = kennelSide.replace(/side/i, '').trim().toUpperCase();
  const sizeLabel = size ? size.charAt(0).toUpperCase() + size.slice(1) : '';
  return sizeLabel ? `${letter} ${sizeLabel}` : letter;
};

const BookingCardKennelList: React.FC<BookingCardKennelListProps> = ({
  kennels,
  bookingId,
  checkInDate,
}) => {
  const theme = useTheme();
  const navigate = useNavigate();

  const handleViewInGrid = (kennel: BookingKennelResponse) => {
    navigate('/kennels', {
      state: {
        highlightedBookingId: bookingId,
        startDate: checkInDate.substring(0, 10),
        tabLabel: deriveTabLabel(kennel.kennelSide, kennel.size),
      },
    });
  };

  if (kennels.length === 0) return null;

  return (
    <Box
      sx={{
        width: '100%',
        maxWidth: '800px',
        '@media (max-width: 600px)': {
          pl: 0,
          '& > div > div': {
            pl: 0,
            pr: 0,
          },
        },
      }}
    >
      {kennels.map((kennel, idx) => {
        // Display heater info inline in header if it exists
        let heaterDisplay = null;
        if (kennel.heaterNights > 0) {
          const heaterTotal = kennel.heaterNights * kennel.heaterDailyRate;
          heaterDisplay = (
            <Box
              sx={{
                display: 'flex',
                alignItems: 'center',
                gap: '0.5rem',
                paddingRight: '1rem',
              }}
            >
              {/* Heater icon - semi-transparent blue */}
              <Box
                sx={{
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  opacity: 0.6,
                  color: theme.palette.primary.main,
                }}
              >
                {getCustomIcon('heater', '2rem')}
              </Box>

              {/* Rate x nights */}
              <span
                style={{
                  fontSize: '1.6rem',
                  color: theme.palette.text.primary,
                }}
              >
                R{kennel.heaterDailyRate.toFixed(2)} x {kennel.heaterNights}
              </span>

              {/* Total heater cost - bold blue */}
              <span
                style={{
                  fontSize: '1.8rem',
                  fontWeight: 700,
                  color: theme.palette.primary.main,
                }}
              >
                {formatCurrency(heaterTotal)}
              </span>
            </Box>
          );
        }
        return (
          <Box
            key={kennel.id}
            sx={{
              marginBottom: 0,
              paddingTop: idx === 0 ? '0.5rem' : '0',
              backgroundColor:
                idx % 2 === 0
                  ? 'transparent'
                  : theme.palette.mode === 'dark'
                    ? 'rgba(33, 150, 243, 0.08)'
                    : 'rgba(0, 0, 0, 0.1)',
              '& > div': {
                borderLeft: 'none',
                backgroundColor: 'transparent',
                borderBottom:
                  idx === kennels.length - 1
                    ? 'none'
                    : `2px dashed ${theme.palette.dividerContrast}`,
                display: 'flex',
                flexDirection: 'row',
                justifyContent: 'space-between',
                alignItems: 'center',
                pl: 1,
                '@media (max-width: 600px)': {
                  pl: '0.5rem!important',
                },
              },
            }}
          >
            <SummaryRow
              label={
                <Box
                  sx={{
                    pt: '4px',
                    display: 'flex',
                    alignItems: 'center',
                    gap: '0.5rem',
                    flex: 1,
                    alignSelf: 'stretch',
                    overflowX: 'auto',
                    overflowY: 'hidden',
                    whiteSpace: 'nowrap',
                    WebkitOverflowScrolling: 'touch',
                    scrollbarWidth: 'none',
                    '&::-webkit-scrollbar': {
                      display: 'none',
                    },
                  }}
                >
                  {/* icon container */}
                  <Box
                    sx={{
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'center',
                      flexShrink: 0,
                    }}
                  >
                    {/* <HomeIcon sx={{ fontSize: '2.5rem' }} /> */}
                    {getCustomIcon('kennel', '2.1rem')}
                  </Box>
                  <span style={{ fontWeight: 600, fontSize: '2rem' }}>
                    {getKennelLabel(kennel)}
                  </span>
                </Box>
              }
              value={
                <Box
                  sx={{
                    display: 'flex',
                    alignItems: 'center',
                  }}
                >
                  {/* <IconButton
                    size="small"
                    onClick={() => handleViewInGrid(kennel)}
                    aria-label="View in kennel grid"
                    sx={{ color: theme.palette.primary.main, mr: 0.5 }}
                  >
                    {getCustomIcon('kennel', '2rem')}
                  </IconButton> */}
                  <Typography
                    onClick={() => handleViewInGrid(kennel)}
                    aria-label="View in kennel grid"
                    sx={{
                      fontSize: '1.8rem',
                      color: theme.palette.primary.main,
                      mr: 1,
                    }}
                  >
                    View
                  </Typography>
                  {heaterDisplay}
                </Box>
              }
            />
          </Box>
        );
      })}

      {/* Total Heater Cost Row */}
      {(() => {
        const totalHeaterCost = kennels.reduce((sum, kennel) => {
          return sum + kennel.heaterNights * kennel.heaterDailyRate;
        }, 0);

        return (
          <Box
            sx={{
              '& > div': {
                borderLeft: 'none',
                pl: 0,
              },
              '& > div > div': {
                pl: 0,
                pr: 0,
              },
              '& > div > div > div': {
                pl: 1.5,
                pr: 2.5,
              },
            }}
          >
            <SummaryRow
              label=""
              value=""
              subRows={[
                {
                  label: '',
                  value: formatCurrency(totalHeaterCost),
                  rowType: 'total',
                },
              ]}
              totalRowBgColor={
                theme.palette.mode === 'dark'
                  ? 'rgba(33, 150, 243, 0.05)'
                  : 'rgba(33, 150, 243, 0.1)'
              }
            />
          </Box>
        );
      })()}
    </Box>
  );
};

export default BookingCardKennelList;
